import type { Map as MapboxMap } from 'mapbox-gl'
import { useEffect, useMemo, useRef } from 'react'

/**
 * Single-hazard renderer for RiskDataDetailPage.
 *
 * Hazards are typically raster tilesets (heatmaps, flood depth, etc.), so this component
 * renders the provided Mapbox tileset id as a raster source + raster layer.
 */
export interface HazardSingleLayerProps {
  map: MapboxMap
  /** Mapbox tileset id e.g. "org.tileset" */
  tilesetId: string | null
  /** Optional stable id used to namespace source/layer ids. Defaults to tilesetId */
  hazardId?: string
  /** Optional namespace prefix to avoid collisions with other maps */
  namespace?: string
  /** Optional opacity for the raster overlay */
  opacity?: number
}

export const HazardSingleLayer = ({
  map,
  tilesetId,
  hazardId,
  namespace = 'detail',
  opacity = 0.85,
}: HazardSingleLayerProps) => {
  const renderedLayerIdsRef = useRef<Set<string>>(new Set())

  const id = useMemo(() => {
    if (!tilesetId) return null
    return hazardId ?? tilesetId
  }, [tilesetId, hazardId])

  useEffect(() => {
    // Always clear previously-rendered layers for this component
    clearHazardLayers(map, namespace)
    renderedLayerIdsRef.current.clear()

    if (!tilesetId || !id) return

    const layerId = addHazardToMap(map, tilesetId, id, namespace, opacity)
    if (layerId) renderedLayerIdsRef.current.add(layerId)

    // Copy ref value for cleanup
    const renderedLayerIds = renderedLayerIdsRef.current

    return () => {
      clearHazardLayers(map, namespace)
      renderedLayerIds.clear()
    }
  }, [map, tilesetId, id, namespace, opacity])

  return null
}

function addHazardToMap(
  map: MapboxMap,
  tilesetId: string,
  hazardId: string,
  namespace: string,
  opacity: number,
): string | null {
  const sourceId = `${namespace}-hazard-source-${hazardId}`
  const layerId = `${namespace}-hazard-layer-${hazardId}`

  const tileUrl = `mapbox://${tilesetId}`

  // Insert below labels for readability (matches ElementSingleLayer)
  const beforeId = map.getStyle().layers?.find((l) => l.type === 'symbol')?.id

  try {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'raster',
        url: tileUrl,
        tileSize: 256,
      })
    }

    if (!map.getLayer(layerId)) {
      map.addLayer(
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': opacity,
          },
        },
        beforeId,
      )
    }

    return layerId
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to add hazard ${hazardId}:`, error)
    return null
  }
}

/**
 * Remove all layers/sources created by HazardSingleLayer for a given namespace.
 */
function clearHazardLayers(map: MapboxMap, namespace: string) {
  const style = map.getStyle()
  if (!style) return

  const layerPrefix = `${namespace}-hazard-layer-`
  const hazardLayers = style.layers?.filter((layer) => layer.id.startsWith(layerPrefix)) || []

  hazardLayers.forEach((layer) => {
    if (map.getLayer(layer.id)) {
      map.removeLayer(layer.id)
    }
  })

  const sourcePrefix = `${namespace}-hazard-source-`
  const hazardSources = Object.keys(style.sources || {}).filter((sourceId) =>
    sourceId.startsWith(sourcePrefix),
  )

  hazardSources.forEach((sourceId) => {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId)
    }
  })
}
